import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlus, 
  faUsers, 
  faSpinner, 
  faSearch,
  faFilter,
  faUser,
  faCalendarAlt,
  faExclamationTriangle,
  faCircleCheck,
  faCircleXmark,
  faCircleExclamation,
  faCheckCircle,
  faTimesCircle,
  faBan,
  faUnlock
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../Api';

const CatiInterviewers = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [interviewers, setInterviewers] = useState([]);
  const [filteredInterviewers, setFilteredInterviewers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [verifiedFilter, setVerifiedFilter] = useState('all');
  const [formationFilter, setFormationFilter] = useState('all');
  const [formations, setFormations] = useState([]);
  const [processingId, setProcessingId] = useState(null);

  // Roles that cannot suspend interviewers
  const canSuspend = user?.role === 'resp_cati';

  // Handle success message from navigation state
  useEffect(() => {
    if (location.state?.successMessage) {
      setSuccessMessage(location.state.successMessage);
      // Clear the success message after 5 seconds
      const timer = setTimeout(() => {
        setSuccessMessage('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [location.state]);

  useEffect(() => {
    fetchInterviewers();
    fetchFormations();
  }, []);

  useEffect(() => {
    filterInterviewers();
  }, [searchTerm, statusFilter, verifiedFilter, formationFilter, interviewers]);

  const fetchInterviewers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/cati/interviewers');
      if (response.data.success) {
        setInterviewers(response.data.data || []);
      } else {
        setInterviewers([]);
      }
    } catch (error) {
      console.error('Error fetching CATI interviewers:', error);
      setError('Erreur lors du chargement des enquêteurs CATI');
      setInterviewers([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchFormations = async () => {
    try {
      const response = await api.get('/formations?is_active=true');
      console.log('Formations API response:', response.data);
      if (response.data.success) {
        const formationsList = response.data.data || [];
        console.log('Formations fetched:', formationsList.length);
        setFormations(formationsList);
      } else {
        console.warn('Formations API returned success=false:', response.data);
        setFormations([]);
      }
    } catch (error) {
      console.error('Error fetching formations:', error);
      console.error('Error response:', error.response?.data);
      // Don't show error for formations, just log it
      setFormations([]);
    }
  };

  const filterInterviewers = () => {
    let filtered = [...interviewers];

    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(interviewer => 
        interviewer.name?.toLowerCase().includes(searchLower) ||
        interviewer.email?.toLowerCase().includes(searchLower) ||
        interviewer.ref?.toLowerCase().includes(searchLower) ||
        interviewer.current_post?.number?.toString().includes(searchTerm) ||
        interviewer.current_post?.label?.toLowerCase().includes(searchLower)
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(interviewer => interviewer.status === statusFilter);
    }

    // Verified filter
    if (verifiedFilter !== 'all') {
      filtered = filtered.filter(interviewer => interviewer.verified === verifiedFilter);
    }

    // Formation filter - check completed formations
    if (formationFilter !== 'all') {
      const formationId = parseInt(formationFilter);
      filtered = filtered.filter(interviewer => {
        // Check if interviewer has this formation completed (either online or presence)
        if (!interviewer.completed_formation_ids || !Array.isArray(interviewer.completed_formation_ids)) {
          return false;
        }
        // Check if formation ID exists in completed formations array
        return interviewer.completed_formation_ids.some(f => 
          parseInt(f) === formationId || f === formationId
        );
      });
    }

    setFilteredInterviewers(filtered);
  };

  const handleToggleStatus = async (interviewerId, currentStatus) => {
    try {
      setProcessingId(interviewerId);
      setError('');
      
      const response = await api.put(`/cati/interviewers/${interviewerId}/toggle-status`);
      
      if (response.data.success) {
        // Update the interviewer in the list
        setInterviewers(prev => 
          prev.map(interviewer => 
            interviewer.id === interviewerId 
              ? { ...interviewer, status: response.data.data.status }
              : interviewer
          )
        );
        
        const action = currentStatus === 'suspended' ? 'réactivé' : 'suspendu';
        setSuccessMessage(`Enquêteur ${action} avec succès`);
        setTimeout(() => setSuccessMessage(''), 5000);
      } else {
        setError(response.data.message || 'Erreur lors de la modification du statut');
      }
    } catch (error) {
      console.error('Error toggling interviewer status:', error);
      setError(
        error.response?.data?.message || 
        'Erreur lors de la modification du statut de l\'enquêteur'
      );
    } finally {
      setProcessingId(null);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return <FontAwesomeIcon icon={faCircleCheck} className="text-green-500" />;
      case 'suspended':
        return <FontAwesomeIcon icon={faCircleXmark} className="text-red-500" />;
      case 'inactive':
        return <FontAwesomeIcon icon={faCircleExclamation} className="text-yellow-500" />;
      default:
        return <FontAwesomeIcon icon={faCircleXmark} className="text-gray-400" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'active':
        return 'Actif';
      case 'suspended':
        return 'Suspendu';
      case 'inactive':
        return 'Inactif';
      default:
        return 'Inconnu';
    }
  };

  const getVerifiedIcon = (verified) => {
    switch (verified) {
      case 'yes':
        return <FontAwesomeIcon icon={faCheckCircle} className="text-green-500" />;
      case 'no':
        return <FontAwesomeIcon icon={faTimesCircle} className="text-red-500" />;
      case 'partially':
        return <FontAwesomeIcon icon={faCircleExclamation} className="text-yellow-500" />;
      default:
        return <FontAwesomeIcon icon={faTimesCircle} className="text-gray-400" />;
    }
  };

  const getVerifiedText = (verified) => {
    switch (verified) {
      case 'yes':
        return 'Vérifié';
      case 'no':
        return 'Non vérifié';
      case 'partially':
        return 'Partiellement';
      default:
        return 'Non vérifié';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 relative overflow-hidden">
        {/* Dotted Background */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle, #dc2626 1px, transparent 1px)',
            backgroundSize: '20px 20px'
          }}></div>
        </div>
        <div className="text-center relative z-10 py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement des enquêteurs CATI...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 relative overflow-hidden">
      {/* Dotted Background */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle, #dc2626 1px, transparent 1px)',
          backgroundSize: '20px 20px'
        }}></div>
      </div>

      {/* Header */}
      <header className="relative z-10 p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center">
            <h1 className="text-3xl font-bold text-gray-800">ENQUÊTEURS CATI</h1>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            {canSuspend && (
              <button 
                onClick={() => navigate('/cati/interviewer/create')}
                className="px-6 py-3 bg-red-600 text-white rounded-xl transition-all duration-200 font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 active:scale-95 active:shadow-md active:translate-y-0 flex items-center justify-center"
              >
                <FontAwesomeIcon icon={faPlus} className="mr-2" />
                Nouveau
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Main content */}
      <div className="relative z-10 p-6">
        {error && (
          <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
            <div className="flex items-center">
              <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
              {error}
            </div>
          </div>
        )}

        {/* Success Message */}
        {successMessage && (
          <div className="mb-6 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg">
            <div className="flex items-center">
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              {successMessage}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 p-6 mb-6">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex items-center space-x-2 flex-shrink-0">
              <FontAwesomeIcon icon={faFilter} className="text-gray-500" />
              <span className="font-medium text-gray-700">Filtres:</span>
            </div>
            
            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none flex-shrink-0 w-full sm:w-auto"
            >
              <option value="all">Tous les statuts</option>
              <option value="active">Actif</option>
              <option value="suspended">Suspendu</option>
              <option value="inactive">Inactif</option>
            </select>

            {/* Verified Filter */}
            <select
              value={verifiedFilter}
              onChange={(e) => setVerifiedFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none flex-shrink-0 w-full sm:w-auto"
            >
              <option value="all">Tous les vérifications</option>
              <option value="yes">Vérifié</option>
              <option value="no">Non vérifié</option>
            </select>

            {/* Formation Filter */}
            <select
              value={formationFilter}
              onChange={(e) => setFormationFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none flex-shrink-0 w-full sm:w-auto"
            >
              <option value="all">Toutes les formations</option>
              {formations.map((formation) => (
                <option key={formation.id} value={formation.id}>
                  {formation.title}
                </option>
              ))}
            </select>

            {/* Search */}
            <div className="relative flex-1 w-full sm:min-w-0 min-w-0">
              <input
                type="text"
                placeholder="Rechercher par nom, email, référence ou poste..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none w-full pr-10 max-w-full"
              />
              <FontAwesomeIcon 
                icon={faSearch} 
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" 
              />
            </div>
          </div>
        </div>

        {/* Interviewers Table */}
        <div className="bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 overflow-hidden">
          {filteredInterviewers.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <FontAwesomeIcon icon={faUsers} className="text-4xl text-gray-300 mb-4" />
              <p className="text-lg">Aucun enquêteur CATI trouvé</p>
              <p className="text-sm">
                {interviewers.length === 0 
                  ? 'Les enquêteurs CATI apparaîtront ici' 
                  : 'Aucun résultat ne correspond à vos critères de recherche'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Enquêteur
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Poste
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Statut
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Vérification
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Dernière connexion
                    </th>
                    {canSuspend && (
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredInterviewers.map((interviewer) => (
                    <tr 
                      key={interviewer.id}
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => navigate(`/cati/interviewers/${interviewer.id}`)}
                    >
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center">
                              <FontAwesomeIcon icon={faUser} className="text-red-600" />
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {interviewer.name}
                            </div>
                            {interviewer.ref && (
                              <div className="text-sm text-gray-500">
                                {interviewer.ref}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        {interviewer.email}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        {interviewer.current_post ? (
                          interviewer.current_post.number
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {getStatusIcon(interviewer.status)}
                          <span className="ml-2 text-sm text-gray-900">
                            {getStatusText(interviewer.status)}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {getVerifiedIcon(interviewer.verified)}
                          <span className="ml-2 text-sm text-gray-900">
                            {getVerifiedText(interviewer.verified)}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                        {interviewer.last_login_at ? (
                          <div className="flex items-center">
                            <FontAwesomeIcon icon={faCalendarAlt} className="mr-2 text-gray-400" />
                            {new Date(interviewer.last_login_at).toLocaleDateString('fr-FR', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </div>
                        ) : (
                          <span className="text-gray-400">Jamais connecté</span>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm">
                        {canSuspend ? (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleStatus(interviewer.id, interviewer.status);
                            }}
                            disabled={processingId === interviewer.id}
                            className={`
                              px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200
                              ${interviewer.status === 'suspended'
                                ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                : 'bg-red-100 text-red-700 hover:bg-red-200'
                              }
                              ${processingId === interviewer.id ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                              disabled:opacity-50 disabled:cursor-not-allowed
                            `}
                            title={interviewer.status === 'suspended' ? 'Réactiver l\'enquêteur' : 'Suspendre l\'enquêteur'}
                          >
                            {processingId === interviewer.id ? (
                              <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                            ) : interviewer.status === 'suspended' ? (
                              <>
                                <FontAwesomeIcon icon={faUnlock} className="mr-1" />
                                Réactiver
                              </>
                            ) : (
                              <>
                                <FontAwesomeIcon icon={faBan} className="mr-1" />
                                Suspendre
                              </>
                            )}
                          </button>
                        ) : (
                          <span className="text-xs text-gray-400">-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Summary */}
        {!loading && (
          <div className="mt-6 bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 p-4">
            <div className="flex items-center text-sm text-gray-700">
              <span>
                {filteredInterviewers.length} enquêteur{filteredInterviewers.length !== 1 ? 's' : ''} CATI
                {filteredInterviewers.length !== interviewers.length && (
                  <span className="text-gray-500">
                    {' '}sur {interviewers.length} au total
                  </span>
                )}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CatiInterviewers;

